import { t } from 'i18next';
import { memo } from 'react';
import { Flow } from '@thive/fddm-types';

import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { NumberInput } from '@/modules/common/components/inputs';
import { useOrderProfileStore } from '@/modules/orderProfile/store/useOrderProfileStore';

import { useFlowUpdaters } from '../hooks/useFlowUpdaters';
import { FlowGraph } from './FlowGraph';
import { LoadPerHourInput } from './LoadPerHourInput';
import { PreAnnouncementInput } from './PreAnnouncementInput';

interface Props {
  id: string;
  disabled: boolean;
  flow: Flow;
}

function ConsolidationStorageProperties({ id, disabled, flow }: Props) {
  const { vehicleLimit, hourlyOrderCounts, consolidationStorageExtension } = flow;
  const totalNumLoads = hourlyOrderCounts[0];
  const { onFlowVehicleLimitChange, onBatchSizeChange, onConsolidationGroupsChange } =
    useFlowUpdaters(id);
  const { orderProfile } = useOrderProfileStore();

  if (!consolidationStorageExtension) return null;

  const { batchSize, numConsolidations } = consolidationStorageExtension;

  return (
    <>
      {orderProfile ? (
        <FlowGraph hourlyOrderCounts={hourlyOrderCounts} />
      ) : (
        <PropertiesLabel
          labelRatio={70}
          i18nkey='interface:properties.flows.flow_info.total_loads.label'
        >
          <LoadPerHourInput id={id} totalNumLoads={totalNumLoads} disabled={disabled} />
        </PropertiesLabel>
      )}
      <PropertiesLabel
        labelRatio={70}
        i18nkey='interface:properties.flows.consolidation.fields.batch_size.label'
      >
        <NumberInput
          value={batchSize}
          minValue={1}
          minValueErrorMsg={t('common:minimum_value', { minValue: 1 })}
          onChange={onBatchSizeChange}
        />
      </PropertiesLabel>
      <PropertiesLabel
        labelRatio={70}
        i18nkey='interface:properties.flows.consolidation.fields.consolidation_groups.label'
      >
        <NumberInput
          value={numConsolidations}
          minValue={1}
          minValueErrorMsg={t('common:minimum_value', { minValue: 1 })}
          onChange={onConsolidationGroupsChange}
        />
      </PropertiesLabel>
      {consolidationStorageExtension &&
        consolidationStorageExtension.preAnnounceDuration !== undefined &&
        consolidationStorageExtension.maxActivePreAnnouncedAvOrders !== undefined && (
          <PreAnnouncementInput
            id={id}
            sourceAreaOrFlowStopId={flow.sourceAreaOrFlowStopId}
            preAnnounceDuration={consolidationStorageExtension.preAnnounceDuration}
            maxActivePreAnnouncedAvOrders={
              consolidationStorageExtension.maxActivePreAnnouncedAvOrders
            }
            disabled={disabled}
          />
        )}
      <PropertiesLabel labelRatio={70} skipFocus i18nkey='interface:properties.highway.vehicles'>
        <NumberInput
          value={vehicleLimit}
          onChange={onFlowVehicleLimitChange}
          clearButton={{
            text: 'All',
            value: -1,
          }}
          sx={{ width: '100%', float: 'right' }}
          disabled={disabled}
        />
      </PropertiesLabel>
    </>
  );
}

export default memo(ConsolidationStorageProperties);
